import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Baie coulissante', to: '/reparation-baie-coulissante-herault/' },
  { label: 'Double vitrage' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Remplacement du vitrage de baie coulissante dans l\'Hérault',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'AdministrativeArea', name: 'Hérault' },
  description: 'Remplacement de vitre cassée, double vitrage embué ou simple vitrage sur baie coulissante dans l\'Hérault.',
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Peut-on remplacer uniquement le vitrage d\'une baie coulissante sans changer le cadre ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, dans la grande majorité des cas. Le vitrage est maintenu dans le vantail par des parcloses qui peuvent être démontées pour accéder au verre. Nous remplaçons le vitrage endommagé par un neuf aux mêmes dimensions, puis reposons les parcloses. L\'opération dure généralement quelques heures et ne nécessite pas de changer le cadre aluminium.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quelle différence entre un double vitrage standard et un vitrage à isolation renforcée ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Le double vitrage standard (deux verres de 4 mm avec lame d\'air de 12 ou 16 mm) offre une bonne isolation. Le vitrage à isolation renforcée (VIR) intègre un traitement basse émissivité sur une face intérieure et un gaz argon entre les deux verres, ce qui améliore significativement les performances thermiques. Pour les baies exposées au sud dans l\'Hérault, un vitrage de contrôle solaire peut également être recommandé.',
      },
    },
  ],
};

const contentBlocks = [
  {
    image: '/images/doublevitrage-block1.jpg',
    imageAlt: 'Double vitrage embue de baie coulissante dans l Herault',
    children: (
      <>
        <h2>Le vitrage, cœur de votre baie coulissante</h2>
        <p>
          Le vitrage représente la majeure partie de la surface d'une baie coulissante. C'est lui qui détermine en grande partie les performances thermiques et phoniques de votre menuiserie, sa luminosité, et le confort de votre intérieur. Un vitrage endommagé — qu'il soit cassé, fissuré ou simplement embué entre les deux verres — doit être remplacé rapidement. DFPRO Hérault intervient sur le remplacement du vitrage de baies coulissantes dans tout le département, de Montpellier au littoral.
        </p>
        <h3>Les différents types de dommages du vitrage</h3>
        <p>
          La casse franche est le cas le plus évident : un choc, un objet projeté par le vent ou une tentative d'effraction peuvent briser le verre. La fissure est plus insidieuse : elle peut apparaître suite à un choc thermique (différence brutale de température entre l'intérieur et l'extérieur) ou à une contrainte mécanique sur le cadre. Le double vitrage embué, ou « en opale », est un autre problème fréquent : le joint périphérique du vitrage isolant a cédé, laissant l'humidité s'infiltrer entre les deux verres. Non seulement l'aspect visuel est dégradé, mais les performances d'isolation sont compromises. Enfin, dans les logements anciens de l'Hérault, on trouve encore des baies coulissantes équipées de simple vitrage, particulièrement énergivores et peu confortables.
        </p>
      </>
    ),
  },
  {
    image: '/images/doublevitrage-block2.jpg',
    imageAlt: 'Technicien DFPRO remplacant le double vitrage d une baie coulissante',
    imagePosition: 'right' as const,
    children: (
      <>
        <h3>Notre processus de remplacement du vitrage</h3>
        <p>
          Le remplacement du vitrage d'une baie coulissante nécessite du savoir-faire et des précautions de sécurité. Nos techniciens commencent par démonter les parcloses — ces baguettes aluminium ou PVC qui maintiennent le verre dans le vantail — avec des outils adaptés pour ne pas endommager le profilé. Le vitrage endommagé est ensuite extrait avec précaution. Les joints d'étanchéité sont inspectés et remplacés si nécessaire. Le nouveau vitrage, fabriqué aux dimensions exactes, est posé, calé, et les parcloses sont remises en place. Un contrôle final vérifie la parfaite étanchéité et l'absence de contrainte sur le verre.
        </p>
        <h3>Quel type de vitrage choisir ?</h3>
        <p>
          Pour le remplacement, nous proposons plusieurs options en fonction de vos besoins. Le double vitrage standard (4/16/4) offre un bon équilibre entre isolation et coût. Le double vitrage à isolation renforcée (VIR), avec traitement basse émissivité et gaz argon, améliore significativement les performances thermiques — un atout dans l'Hérault où les étés sont longs et chauds. Pour les baies exposées plein sud à Montpellier ou dans les communes du littoral, un vitrage de contrôle solaire peut limiter la surchauffe estivale. En cas de remplacement d'un simple vitrage par du double vitrage, nous vérifions que le vantail est compatible en termes d'épaisseur. Dans tous les cas, notre technicien vous conseillera la solution la plus adaptée.
        </p>
        <h3>Sécurité et délais</h3>
        <p>
          Un vitrage cassé présente un risque immédiat, en particulier si des enfants ou des animaux domestiques évoluent à proximité. Nous traitons ces situations en priorité : selon la disponibilité du verre aux dimensions requises, l'intervention peut être réalisée sous 24 à 48h. En attendant, nous vous conseillons de sécuriser la zone et, si possible, de protéger provisoirement l'ouverture avec un panneau rigide. Pour un double vitrage embué, l'urgence est moindre mais il ne faut pas trop attendre : un vitrage isolant défaillant augmente votre consommation énergétique.
        </p>
        <p>
          Pour toute information complémentaire ou pour obtenir un devis gratuit, contactez-nous au 09 80 80 22 42. Consultez également notre page <Link to="/reparation-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réparation de baie coulissante</Link> pour découvrir l'ensemble de nos services.
        </p>
      </>
    ),
  },
];

export default function DoubleVitrage() {
  return (
    <>
      <SEO
        title="Double vitrage de baie coulissante Hérault | DFPRO"
        description="Vitre cassée ou double vitrage embué sur une baie coulissante dans l'Hérault ? Remplacement rapide du vitrage. Devis gratuit, garantie 10 ans. 09 80 80 22 42."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/double-vitrage-baie-coulissante-herault/"
      />
      <ServicePageLayout
        title="Remplacement du vitrage d'une baie coulissante dans l'Hérault"
        subtitle="Vitre cassée, double vitrage embué ou simple vitrage à remplacer ? Nos techniciens changent le vitrage de votre baie coulissante."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        heroImage="/images/doublevitrage-hero.jpg"
        heroImageAlt="Double vitrage de baie coulissante endommage DFPRO Herault"
        contentBlocks={contentBlocks}
      />
    </>
  );
}